import { useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { adminApi } from "@/lib/api";

export const CRM_STATUSES = [
  { id: "nuovo", label: "Nuovo", color: "#71717a" },
  { id: "contattato", label: "Contattato", color: "#3b82f6" },
  { id: "interessato", label: "Interessato", color: "#f59e0b" },
  { id: "chiuso", label: "Chiuso", color: "#10b981" },
];

export const crmMeta = (id) => CRM_STATUSES.find((s) => s.id === id) || CRM_STATUSES[0];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

export const CrmStatusSelect = ({ lead, onUpdated }) => {
  const meta = crmMeta(lead.crm_status);
  const change = async (crm_status) => {
    try {
      onUpdated(await adminApi.updateCrm(lead.id, { crm_status }));
      toast.success(`Stato: ${crmMeta(crm_status).label}`);
    } catch {
      toast.error("Errore aggiornamento CRM");
    }
  };
  return (
    <div>
      <select data-testid={`crm-status-${lead.id}`} value={meta.id} onChange={(e) => change(e.target.value)}
        style={{ borderColor: meta.color, color: meta.color }}
        className="h-7 bg-transparent border text-[11px] font-mono uppercase px-1.5 rounded-sm cursor-pointer">
        {CRM_STATUSES.map((s) => <option key={s.id} value={s.id} className="bg-[#f4f6f7] text-[#2B3036]">{s.label}</option>)}
      </select>
      <div className="text-[10px] font-mono text-[#6b7280] mt-1">ult. contatto {fmtDate(lead.last_contact_at)}</div>
    </div>
  );
};

export const CrmModal = ({ lead, onClose, onUpdated }) => {
  const [status, setStatus] = useState(lead.crm_status || "nuovo");
  const [note, setNote] = useState(lead.crm_note || "");
  const [date, setDate] = useState(lead.last_contact_at ? lead.last_contact_at.slice(0, 10) : "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { crm_status: status, crm_note: note };
      if (date) payload.last_contact_at = new Date(date).toISOString();
      onUpdated(await adminApi.updateCrm(lead.id, payload));
      toast.success("Scheda CRM aggiornata");
      onClose();
    } catch {
      toast.error("Errore salvataggio CRM");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-6" onClick={onClose}>
      <div data-testid="crm-modal" className="bg-white border border-[#d5dbde] max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#d5dbde] flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl">Scheda CRM</h3>
            <p className="text-xs text-[#6b7280] font-mono mt-0.5">{lead.company_name} · {lead.city}</p>
          </div>
          <button data-testid="crm-modal-close" onClick={onClose} className="text-[#6b7280] hover:text-[#E0262C]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6b7280] font-mono mb-2">Stato pipeline</div>
            <div className="grid grid-cols-4 gap-2">
              {CRM_STATUSES.map((s) => (
                <button key={s.id} data-testid={`crm-modal-status-${s.id}`} onClick={() => setStatus(s.id)}
                  style={status === s.id ? { borderColor: s.color, color: s.color, background: `${s.color}1a` } : undefined}
                  className="border border-[#d5dbde] text-[#5b6168] py-2 text-xs font-medium transition-colors hover:border-[#52525b]">
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6b7280] font-mono mb-2">Nota breve</div>
            <textarea data-testid="crm-modal-note" value={note} maxLength={500} rows={4} onChange={(e) => setNote(e.target.value)}
              placeholder="Es. richiamare martedì, interessato al pacchetto Meta..."
              className="w-full p-3 bg-[#f4f6f7] border border-[#d5dbde] text-[#2B3036] rounded-sm text-sm" />
            <div className="text-[10px] font-mono text-[#52525b] text-right">{note.length}/500</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6b7280] font-mono mb-2">Data ultimo contatto</div>
            <input data-testid="crm-modal-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-10 bg-[#f4f6f7] border border-[#d5dbde] text-[#2B3036] rounded-sm text-sm px-3 [color-scheme:dark]" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#d5dbde] flex justify-end gap-3">
          <Button onClick={onClose} variant="outline" className="bg-transparent border-[#d5dbde] text-[#2B3036] hover:bg-[#f4f6f7]">Annulla</Button>
          <Button data-testid="crm-modal-save" onClick={save} disabled={saving} className="aurora-gradient hover:opacity-90 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ReviewsCell = ({ lead }) => {
  const summary = (
    <>
      <div className="flex items-center gap-1 text-[#f59e0b]"><Star className="w-3 h-3 fill-current" /> {lead.reviews_rating || "-"}</div>
      <div className="text-[10px] font-mono text-[#6b7280]">{lead.reviews_count || 0} recensioni</div>
    </>
  );
  if (!lead.reviews?.length) return summary;
  return (
    <Popover>
      <PopoverTrigger data-testid={`reviews-${lead.id}`} className="text-left hover:opacity-80">
        {summary}
        <div className="text-[10px] font-mono text-[#E0262C] underline">leggi Google</div>
      </PopoverTrigger>
      <PopoverContent className="w-80 bg-white border-[#d5dbde] text-[#2B3036] max-h-80 overflow-y-auto p-0">
        {lead.reviews.map((r, i) => (
          <div key={i} className="p-3 border-b border-[#d5dbde] last:border-0 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium">{r.author || "Utente Google"}</span>
              <span className="text-[#f59e0b] font-mono">{"★".repeat(r.rating || 0)}</span>
            </div>
            <p className="text-[#5b6168] leading-relaxed">{r.text}</p>
            <div className="text-[10px] font-mono text-[#52525b] mt-1">{r.when}</div>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const VERIF = {
  valid: ["verificata", "#10b981"], invalid: ["non valida", "#ef4444"], accept_all: ["catch-all", "#f59e0b"],
  webmail: ["webmail", "#3b82f6"], disposable: ["usa e getta", "#ef4444"], unknown: ["sconosciuta", "#71717a"],
};

export const EmailVerifBadge = ({ status }) => {
  if (!status || !VERIF[status]) return null;
  const [label, color] = VERIF[status];
  return <span style={{ color, borderColor: color }} className="inline-block mt-0.5 border text-[9px] font-mono uppercase px-1">{label}</span>;
};
